import React, { useEffect, useState } from "react";
import { Box, Typography, TextField, Button, Grid } from "@mui/material";
import Header from "../../Header/Header";
import { useNavigate } from "react-router-dom";
import { postApi } from "../../AppApis";
import { toast } from "react-toastify";
import { FiEdit, FiSave } from "react-icons/fi";
import { showSuccessToast } from "../../utils";
import { formatUSAPrice } from "../../../helpers/utils";

const validationRules = {
    generalInfo: {
        firstName: {
            required: true,
            minLength: 2,
            maxLength: 50,
            pattern: /^[A-Za-z]+$/,
            errorMessage: "Please enter a valid first name (letters only, 2-50 characters)",
        },
        lastName: {
            required: true,
            minLength: 2,
            maxLength: 50,
            pattern: /^[A-Za-z]+$/,
            errorMessage: "Please enter a valid last name (letters only, 2-50 characters)",
        },
        phoneNumber: {
            required: true,
            pattern: /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
            errorMessage: "Please enter a valid phone number (e.g., 123-456-7890)",
        },
        email: {
            required: true,
            pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            errorMessage: "Please enter a valid email address",
        },
    },
    coveragePreferences: {
        salary: {
            required: true,
            pattern: /^\d+$/,
            errorMessage: "Please enter a valid amount (numbers only)",
        },
        deductible: {
            required: true,
            pattern: /^\d+$/,
            errorMessage: "Please enter a valid amount (numbers only)",
        },
    },
    propertyInfo: {
        address: {
            required: true,
            minLength: 5,
            maxLength: 200,
            errorMessage: "Please enter a valid address (5-200 characters)",
        },
        city: {
            required: true,
            minLength: 2,
            maxLength: 50,
            pattern: /^[A-Za-z\s]+$/,
            errorMessage: "Please enter a valid city name",
        },
        state: {
            required: true,
            errorMessage: "Please enter a valid state",
        },
        zipCode: {
            required: true,
            pattern: /^\d{5}(-\d{4})?$/,
            errorMessage: "Please enter a valid ZIP code (5 or 9 digits)",
        },
    },
    additionalDetails: {
        hasFiledClaim: {
            required: true,
            errorMessage: "Please answer as yes or no",
        },
        requiresFloodInsurance: {
            required: true,
            errorMessage: "Please answer as yes or no",
        },
    },
};

const StepFiveInsurance = () => {
    const storageInfo = JSON.parse(sessionStorage.getItem("insuranceInfo"));
    const [formData, setFormData] = useState(storageInfo);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({
        generalInfo: {},
        coveragePreferences: {},
        propertyInfo: {},
        additionalDetails: {},
    });
    const [editSections, setEditSections] = useState({
        generalInfo: false,
        titleServiceNeeds: false,
        propertyInfo: false,
        additionalInfo: false,
    });

    const navigate = useNavigate();

    useEffect(() => {
        sessionStorage.setItem("insuranceInfo", JSON.stringify(formData));
    }, [formData]);

    const formatPhoneNumber = (value) => {
        if (!value) return value;
        const phoneNumber = value.replace(/[^\d]/g, "");
        const phoneNumberLength = phoneNumber.length;
        if (phoneNumberLength < 4) return phoneNumber;
        if (phoneNumberLength < 7) {
            return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
        }
        return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    };

    const formatZipCode = (value) => {
        if (!value) return value;
        const zip = value.replace(/[^\d]/g, "");
        const zipLength = zip.length;
        if (zipLength <= 5) return zip;
        return `${zip.slice(0, 5)}-${zip.slice(5, 9)}`;
    };

    const formatCurrency = (value) => {
        return value.replace(/[^\d]/g, "");
    };

    const validateField = (section, field, value) => {
        const rules = validationRules[section]?.[field];
        const newErrors = { ...errors };

        if (!rules) return true;

        if (newErrors[section]?.[field]) {
            delete newErrors[section][field];
            setErrors(newErrors);
        }

        if (rules.required && !value) {
            setErrors((prev) => ({
                ...prev,
                [section]: { ...prev[section], [field]: rules.errorMessage },
            }));
            return false;
        }

        if (rules.minLength && value.length < rules.minLength) {
            setErrors((prev) => ({
                ...prev,
                [section]: { ...prev[section], [field]: rules.errorMessage },
            }));
            return false;
        }

        if (rules.maxLength && value.length > rules.maxLength) {
            setErrors((prev) => ({
                ...prev,
                [section]: { ...prev[section], [field]: rules.errorMessage },
            }));
            return false;
        }

        if (rules.pattern && !rules.pattern.test(value)) {
            setErrors((prev) => ({
                ...prev,
                [section]: { ...prev[section], [field]: rules.errorMessage },
            }));
            return false;
        }

        if (rules.min !== undefined && Number(value) < rules.min) {
            setErrors((prev) => ({
                ...prev,
                [section]: { ...prev[section], [field]: rules.errorMessage },
            }));
            return false;
        }

        if (rules.max !== undefined && Number(value) > rules.max) {
            setErrors((prev) => ({
                ...prev,
                [section]: { ...prev[section], [field]: rules.errorMessage },
            }));
            return false;
        }

        return true;
    };

    const validateSection = (section) => {
        let isValid = true;
        const sectionData = formData[section];

        Object.keys(sectionData).forEach((field) => {
            if (!validateField(section, field, sectionData[field])) {
                isValid = false;
            }
        });

        return isValid;
    };

    const validateAll = () => {
        let isValid = true;
        let allErrors = [];

        Object.keys(formData).forEach((section) => {
            const sectionData = formData[section];
            Object.keys(sectionData).forEach((field) => {
                if (!validateField(section, field, sectionData[field])) {
                    isValid = false;
                    const message = validationRules[section]?.[field]?.errorMessage;
                    if (message) allErrors.push(message);
                }
            });
        });

        return { isValid, allErrors };
    };

    const handleInputChange = (section, field, value) => {
        // Apply formatting based on field type
        if (field === "phoneNumber") {
            value = formatPhoneNumber(value);
        } else if (field === "zipCode") {
            value = formatZipCode(value);
        } else if (field === "salary" || field === "deductible") {
            value = formatCurrency(value);
        }

        validateField(section, field, value);

        setFormData((prev) => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value,
            },
        }));
    };

    const toggleEditSection = (section) => {
        if (editSections[section] && !validateSection(section)) {
            toast.error("Please fix validation errors before saving", { toastId: "errorToast" });
            return;
        }
        setEditSections((prev) => ({
            ...prev,
            [section]: !prev[section],
        }));
    };

    const handleSubmit = async () => {
        const { isValid, allErrors } = validateAll();

        if (!isValid) {
            if (allErrors.length > 0) {
                toast.error(allErrors[0], { toastId: "errorToast" }); // show the first specific error
            } else {
                toast.error("Please fix all validation errors before submitting", { toastId: "errorToast" });
            }
            return;
        }

        setIsLoading(true);
        try {
            const response = await postApi(`/property-services/request_service`, {
                service_name: "InsuranceQuote",
                data: { ...formData, project_type: "airebrokers" },
            });

            if (response.response) {
                showSuccessToast("Form submitted successfully");
                navigate("/services/insurance-quote/confirmation");
            }
        } catch (error) {
            toast.error("Failed to submit form. Please try again.", { toastId: "errorToast" });
        } finally {
            setIsLoading(false);
        }
    };

    const renderSection = (section, title, fields) => {
        const isEditing = editSections[section];

        return (
            <div className="tw-bg-white tw-p-3 tw-rounded-md tw-mt-4">
                <div className="tw-flex tw-justify-between tw-items-center">
                    <Typography variant="h6">{title}</Typography>
                    <div>
                        <div
                            onClick={() => toggleEditSection(section)}
                            className="tw-flex tw-items-center tw-px-2 tw-py-[1px] tw-rounded-[5px] tw-border tw-border-[#000] tw-cursor-pointer"
                        >
                            {isEditing ? <FiSave className="tw-mr-1" size={16} /> : <FiEdit className="tw-mr-1" size={16} />}
                            {isEditing ? "Save" : "Edit"}
                        </div>
                    </div>
                </div>
                <div className="tw-mt-2 tw-rounded-lg tw-flex tw-flex-col tw-gap-2">
                    {Object.entries(fields).map(([fieldName, label]) => (
                        <Box key={fieldName} sx={{ mb: 0 }}>
                            <Box
                                sx={{
                                    bgcolor: "background.paper",
                                    borderColor: "divider",
                                    display: "flex",
                                    textWrap: "nowrap",
                                    color: "#666666",
                                    flexDirection: isEditing ? "column" : "row",
                                    alignItems: isEditing ? "flex-start" : "center",
                                }}
                            >
                                <Typography
                                    sx={{ fontSize: "12px" }}
                                    className={`tw-h-full px-2 py-1 tw-text-wrap tw-w-full ${isEditing ? "" : "tw-font-[600]"}`}
                                >
                                    {label}
                                </Typography>

                                {isEditing ? (
                                    <>
                                        <TextField
                                            fullWidth
                                            variant="standard"
                                            sx={{
                                                "& .MuiInputBase-input": { fontSize: "12px" },
                                            }}
                                            InputProps={{ disableUnderline: false }}
                                            className="px-2"
                                            value={formData[section][fieldName]}
                                            onChange={(e) => handleInputChange(section, fieldName, e.target.value)}
                                            error={!!errors[section]?.[fieldName]}
                                            helperText={errors[section]?.[fieldName]}
                                        />
                                    </>
                                ) : (
                                    <Typography sx={{ fontSize: "12px" }} className="tw-w-full tw-border-b-1 tw-h-full px-2 py-1 tw-border-white">
                                        {fieldName === "address"
                                            ? formData[section][fieldName].length > 40
                                                ? `${formData[section][fieldName].slice(0, 40)}...`
                                                : formData[section][fieldName]
                                            : fieldName === "salary" || fieldName === "deductible"
                                              ? formatUSAPrice(formData[section][fieldName])
                                              : formData[section][fieldName]}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="tw-min-h-screen tw-bg-background_color">
            <Header title="Insurance Quote" />
            <Box component="form" className="tw-mt-[55px]" sx={{ maxWidth: 1000, mx: "auto", p: 4 }}>
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                        Almost There! Review Your Details!
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        You're just one step away from securing expert title services. Please review all details carefully—accuracy at this stage
                        ensures a smooth and efficient process.
                    </Typography>
                </Box>

                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                        {renderSection("generalInfo", "General Information", {
                            firstName: "First name",
                            lastName: "Last name",
                            phoneNumber: "Phone number",
                            email: "Email Address",
                        })}
                        {renderSection("coveragePreferences", "Coverage Options", {
                            salary: "Coverage Amount",
                            deductible: "Deductible",
                        })}
                    </Grid>

                    <Grid item xs={12} md={6}>
                        {renderSection("propertyInfo", "Property Information", {
                            address: "Property Address",
                            city: "City",
                            state: "State",
                            zipCode: "ZIP Code",
                        })}
                    </Grid>
                    <Grid item xs={12} md={12}>
                        {renderSection("additionalDetails", "Additional Details", {
                            hasFiledClaim: "Have you filed an insurance claim in the last 5 years?",
                            requiresFloodInsurance: "Do you require flood insurance?",
                        })}
                    </Grid>
                </Grid>

                <div className="tw-flex tw-justify-center tw-m-5">
                    <button
                        onClick={handleSubmit}
                        disabled={isLoading}
                        type="button"
                        className="tw-bg-primary hover:tw-bg-primary_hover max-sm:tw-w-full tw-py-2 tw-px-10 max-sm:tw-text-nowrap tw-text-white tw-rounded-md"
                    >
                        {isLoading ? "Loading..." : "Get My Personalized Quotes"}
                    </button>
                </div>
            </Box>
        </div>
    );
};

export default StepFiveInsurance;
